import { v4 as uuidv4 } from "uuid";
import { QuestionType } from "api/landlord-surveys/types/questions";
import {
  DetailedLandlordSurvey,
  DirtyField,
  QuestionPayload
} from "api/landlord-surveys/types/types";
import { create } from "zustand";
import {
  DraftDetailedLandlordSurvey,
  DraftSurveyTemplateCondition,
  DraftSurveyTemplateQuestion,
  OptionItem
} from "./types";

const toOptionItems = (strings: string[]): [OptionItem, ...OptionItem[]] =>
  strings.map((value) => ({ id: uuidv4(), value })) as [
    OptionItem,
    ...OptionItem[]
  ];

export type ActivePage = "start" | "end" | "question";

type Store = {
  data: DraftDetailedLandlordSurvey | null;
  apiData: DraftDetailedLandlordSurvey | null;
  isDirty: boolean;
  dirtyFields: Set<DirtyField>;

  activePage: ActivePage;
  activeQuestionIdx: number | null;

  setActivePage: (page: ActivePage) => void;
  resetData: () => void;
  resetApiData: (
    data: DetailedLandlordSurvey | DraftDetailedLandlordSurvey | null
  ) => void;
  updateData: (data: DraftDetailedLandlordSurvey, dirty?: DirtyField[]) => void;
  updateActiveQuestionIdx: (activeQuestionIdx: number | null) => void;

  updateQuestionOrderByIdx: (prevIdx: number, newIdx: number) => void;

  updateActiveQuestion: (
    question:
      | DraftSurveyTemplateQuestion
      | ((prev: DraftSurveyTemplateQuestion) => DraftSurveyTemplateQuestion)
  ) => void;

  duplicateQuestion: () => void;
  addNewQuestion: (index?: number) => void;
  deleteQuestion: () => void;

  activeConditionIdx: number | null;
  updateActiveConditionIdx: (idx: number | null) => void;
  addNewCondition: () => void;
  deleteCondition: (idx: number) => void;
  updateActiveCondition: (
    condition:
      | DraftSurveyTemplateCondition
      | ((prev: DraftSurveyTemplateCondition) => DraftSurveyTemplateCondition)
  ) => void;

  validationErrors: ValidationError[];
  setValidationErrors: (errors: ValidationError[]) => void;
};

const markDirty = (
  state: Store,
  ...fields: DirtyField[]
): { isDirty: true; dirtyFields: Set<DirtyField> } => {
  const next = new Set(state.dirtyFields);
  for (const f of fields) next.add(f);
  return { isDirty: true, dirtyFields: next };
};

export const selectActiveQuestion = (
  state: Store
): DraftSurveyTemplateQuestion | undefined =>
  state.data?.questions.find((q) => q.index === state.activeQuestionIdx);

export const selectSortedQuestions = (
  state: Store
): DraftSurveyTemplateQuestion[] =>
  state.data?.questions.slice().sort((a, b) => a.index - b.index) ?? [];

export const selectCanPreview = (state: Store): boolean => {
  const questions = state.data?.questions ?? [];
  return (
    questions.length > 0 && questions.every((q) => q.question.trim() !== "")
  );
};

export const selectConditions = (
  state: Store
): DraftSurveyTemplateCondition[] => state.data?.conditions ?? [];

export const selectActiveCondition = (
  state: Store
): DraftSurveyTemplateCondition | undefined =>
  state.activeConditionIdx !== null
    ? (state.data?.conditions ?? [])[state.activeConditionIdx]
    : undefined;

const OPTION_QUESTION_TYPES = new Set([
  QuestionType.YES_NO,
  QuestionType.RADIO_GROUP,
  QuestionType.CHECKBOX_MULTI
]);

export type ValidationField =
  | "title"
  | "options"
  | "parent_question"
  | "condition_type"
  | "answer"
  | "action"
  | "child_questions";

export type ValidationError = {
  type: "survey" | "question" | "condition";
  index: number;
  field: ValidationField | "name";
  message: string;
};

const validateHideChildren = (
  c: DraftSurveyTemplateCondition,
  i: number,
  label: string,
  parentIndex: number,
  questionMap: Map<string, DraftSurveyTemplateQuestion>,
  errors: ValidationError[]
) => {
  if (c.child_question_internal_ids.length === 0)
    errors.push({
      type: "condition",
      index: i,
      field: "child_questions",
      message: `${label}: no target questions selected to hide`
    });

  c.child_question_internal_ids.forEach((childId) => {
    const childQ = questionMap.get(childId);
    if (!childQ)
      errors.push({
        type: "condition",
        index: i,
        field: "child_questions",
        message: `${label}: target question no longer exists`
      });
    else if (childQ.index <= parentIndex)
      errors.push({
        type: "condition",
        index: i,
        field: "child_questions",
        message: `${label}: cannot hide "${childQ.question || "Question " + (childQ.index + 1)}" — it comes before the "if" question`
      });
  });
};

const validateCondition = (
  c: DraftSurveyTemplateCondition,
  i: number,
  seenConditions: Map<string, number>,
  questionMap: Map<string, DraftSurveyTemplateQuestion>,
  errors: ValidationError[]
) => {
  const label = `Condition ${i + 1}`;

  // Duplicate condition check
  const childIds = c.child_question_internal_ids
    .slice()
    .sort((a, b) => a.localeCompare(b))
    .join(",");
  const answer = (c.condition.answer as string | undefined) ?? "";
  const condType = (c.condition.type as string | undefined) ?? "";
  const key = `${c.parent_question_internal_id}|${condType}|${answer}|${c.action}|${childIds}`;
  const prevIdx = seenConditions.get(key);
  if (prevIdx !== undefined)
    errors.push({
      type: "condition",
      index: i,
      field: "parent_question",
      message: `${label}: duplicate of Condition ${prevIdx + 1}`
    });
  else seenConditions.set(key, i);

  // Parent question must be set and exist
  if (!c.parent_question_internal_id)
    errors.push({
      type: "condition",
      index: i,
      field: "parent_question",
      message: `${label}: no "if" question selected`
    });
  else if (!questionMap.has(c.parent_question_internal_id))
    errors.push({
      type: "condition",
      index: i,
      field: "parent_question",
      message: `${label}: "if" question no longer exists`
    });

  // Condition type must be set
  const conditionType = c.condition.type as string | undefined;
  if (!conditionType)
    errors.push({
      type: "condition",
      index: i,
      field: "condition_type",
      message: `${label}: condition type is not set`
    });

  // For option-based questions, answer must be selected
  const parentQ = questionMap.get(c.parent_question_internal_id);
  if (parentQ && OPTION_QUESTION_TYPES.has(parentQ.type)) {
    const condAnswer = c.condition.answer as string | undefined;
    if (!condAnswer)
      errors.push({
        type: "condition",
        index: i,
        field: "answer",
        message: `${label}: no answer selected for "${parentQ.question || "Question " + (parentQ.index + 1)}"`
      });
    else if (
      "options" in parentQ &&
      parentQ.options &&
      !parentQ.options.some((o) => o.value === condAnswer)
    )
      errors.push({
        type: "condition",
        index: i,
        field: "answer",
        message: `${label}: answer "${condAnswer}" is no longer a valid option`
      });
  }

  // Action must be set
  if (!c.action)
    errors.push({
      type: "condition",
      index: i,
      field: "action",
      message: `${label}: no action selected (hide / end survey)`
    });

  if (c.action === "hide")
    validateHideChildren(
      c,
      i,
      label,
      parentQ?.index ?? -1,
      questionMap,
      errors
    );

  if (c.action === "end_survey" && c.child_question_internal_ids.length > 0)
    errors.push({
      type: "condition",
      index: i,
      field: "child_questions",
      message: `${label}: "end survey" should not have target questions`
    });
};

export const validateSurvey = (state: Store): ValidationError[] => {
  const errors: ValidationError[] = [];
  const data = state.data;
  if (!data) return errors;

  // ── Survey name validation ──
  if (!data.name.trim())
    errors.push({
      type: "survey",
      index: 0,
      field: "name",
      message: "Survey name is empty"
    });

  const questions = data.questions.slice().sort((a, b) => a.index - b.index);
  const questionMap = new Map(questions.map((q) => [q.internal_id, q]));

  // ── Question validation ──
  questions.forEach((q, i) => {
    if (!q.question.trim())
      errors.push({
        type: "question",
        index: i,
        field: "title",
        message: `Question ${i + 1}: title is empty`
      });

    // Options validation for option-based questions
    if ("options" in q && Array.isArray(q.options)) {
      const seenOptions = new Map<string, number>();
      (q.options as OptionItem[]).forEach((opt, optIdx) => {
        if (!opt.value.trim())
          errors.push({
            type: "question",
            index: i,
            field: "options",
            message: `Question ${i + 1}: option ${optIdx + 1} is empty`
          });
        else {
          const trimmed = opt.value.trim();
          const prevOptIdx = seenOptions.get(trimmed);
          if (prevOptIdx !== undefined)
            errors.push({
              type: "question",
              index: i,
              field: "options",
              message: `Question ${i + 1}: option "${trimmed}" is duplicated (option ${prevOptIdx + 1} and ${optIdx + 1})`
            });
          else seenOptions.set(trimmed, optIdx);
        }
      });
    }
  });

  // ── Condition validation ──
  const conditions = data.conditions ?? [];
  const seenConditions = new Map<string, number>();
  conditions.forEach((c, i) => {
    validateCondition(c, i, seenConditions, questionMap, errors);
  });

  return errors;
};

export const hasValidationError = (
  errors: ValidationError[],
  type: ValidationError["type"],
  index: number,
  field?: ValidationError["field"]
): boolean =>
  errors.some(
    (e) =>
      e.type === type &&
      e.index === index &&
      (field === undefined || e.field === field)
  );

export const toConditionPayload = ({
  internal_id: _,
  ...rest
}: DraftSurveyTemplateCondition) => rest;

export const toQuestionPayload = (
  q: DraftSurveyTemplateQuestion
): QuestionPayload => ({
  internal_id: q.internal_id,
  index: q.index,
  question: q.question,
  description: q.description,
  type: q.type,
  is_required: q.is_required,
  ...("options" in q && q.options
    ? { options: { values: q.options.map((o: OptionItem) => o.value) } }
    : {})
});

export const useSurveyStore = create<Store>((set) => ({
  data: null,
  apiData: null,
  isDirty: false,
  dirtyFields: new Set(),

  activePage: "start" as ActivePage,
  activeQuestionIdx: null,

  setActivePage: (page: ActivePage) => {
    set({ activePage: page, activeQuestionIdx: null });
  },

  validationErrors: [],
  setValidationErrors: (errors: ValidationError[]) => {
    set({ validationErrors: errors });
  },

  updateQuestionOrderByIdx: (prevIdx: number, newIdx: number) => {
    set((state) => {
      if (!state.data) return state;

      const questions = state.data.questions
        .slice()
        .sort((a, b) => a.index - b.index);

      const [moved] = questions.splice(prevIdx, 1);
      questions.splice(newIdx, 0, moved);

      const reorderedQuestions = questions.map((q, i) => ({
        ...q,
        index: i
      }));

      return {
        ...state,
        data: {
          ...state.data,
          questions: reorderedQuestions
        },
        activeQuestionIdx: newIdx,
        ...markDirty(state, "questions")
      };
    });
  },
  resetData: () => {
    set((state) => ({
      ...state,
      data: state.apiData,
      activePage: "start",
      activeQuestionIdx: null,
      activeConditionIdx: null,
      isDirty: false,
      dirtyFields: new Set(),
      validationErrors: []
    }));
  },
  resetApiData: (raw) => {
    const data = raw
      ? {
          ...raw,
          questions: raw.questions.map((q): DraftSurveyTemplateQuestion => {
            if ("options" in q && q.options) {
              const raw = q.options;
              // Already hydrated (OptionItem[])
              if (typeof raw[0] === "object")
                return q as DraftSurveyTemplateQuestion;
              // API format: string[] or { values: string[] }
              const strings: string[] = Array.isArray(raw)
                ? (raw as string[])
                : (raw as unknown as { values: string[] }).values;
              const { options: _rawOpts, ...base } = q;
              return {
                ...base,
                options: toOptionItems(strings)
              } as DraftSurveyTemplateQuestion;
            }
            return q as DraftSurveyTemplateQuestion;
          }),
          conditions: raw.conditions?.map((c) => ({
            ...c,
            internal_id: uuidv4()
          }))
        }
      : null;
    set(() => ({
      data,
      apiData: data,
      isDirty: false,
      dirtyFields: new Set(),
      activePage: "start",
      activeQuestionIdx: null,
      activeConditionIdx: null,
      validationErrors: []
    }));
  },
  updateActiveQuestionIdx: (activeQuestionIdx: number | null) => {
    set((state) => ({ ...state, activeQuestionIdx, activePage: "question" }));
  },
  updateData: (
    data:
      | DraftDetailedLandlordSurvey
      | ((
          prev: DraftDetailedLandlordSurvey | null
        ) => DraftDetailedLandlordSurvey | null),
    dirty?: DirtyField[]
  ) => {
    set((state) => {
      const nextData = typeof data === "function" ? data(state.data) : data;
      return {
        ...state,
        data: nextData,
        validationErrors: [],
        ...markDirty(state, ...(dirty ?? []))
      };
    });
  },
  updateActiveQuestion: (
    question:
      | DraftSurveyTemplateQuestion
      | ((prev: DraftSurveyTemplateQuestion) => DraftSurveyTemplateQuestion)
  ) => {
    set((state) => {
      if (!state.data) return state;

      const prev = state.data.questions.find(
        (q) => q.index === state.activeQuestionIdx
      );
      if (!prev) return state;

      const updated =
        typeof question === "function" ? question(prev) : question;

      return {
        ...state,
        ...markDirty(state, "questions"),
        validationErrors: [],
        data: {
          ...state.data,
          questions: state.data.questions.map((q) =>
            q.index === updated.index ? updated : q
          )
        }
      };
    });
  },

  duplicateQuestion: () => {
    set((state) => {
      if (!state.data || state.activeQuestionIdx === null) return state;

      const source = state.data.questions.find(
        (q) => q.index === state.activeQuestionIdx
      );
      if (!source) return state;

      const newIndex = state.data.questions.length;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- strip server id from duplicate
      const { id, ...rest } = source;
      const base = { ...rest, internal_id: uuidv4(), index: newIndex };
      const duplicate: DraftSurveyTemplateQuestion =
        "options" in base && base.options
          ? ({
              ...base,
              options: base.options.map((o) => ({
                ...o,
                id: uuidv4()
              })) as [OptionItem, ...OptionItem[]]
            } as DraftSurveyTemplateQuestion)
          : (base as DraftSurveyTemplateQuestion);

      return {
        ...state,
        ...markDirty(state, "questions"),
        activeQuestionIdx: newIndex,
        data: {
          ...state.data,
          questions: [...state.data.questions, duplicate]
        }
      };
    });
  },

  addNewQuestion: (index?: number) => {
    set((state) => {
      if (!state.data) return state;

      const questions = state.data.questions
        .slice()
        .sort((a, b) => a.index - b.index);

      const insertAt = index ?? questions.length;

      const newQuestion: DraftSurveyTemplateQuestion = {
        internal_id: uuidv4(),
        index: insertAt,
        question: "",
        description: "",
        is_required: true,
        type: QuestionType.LONG_TEXT
      };

      questions.splice(insertAt, 0, newQuestion);
      const reindexed = questions.map((q, i) => ({ ...q, index: i }));

      return {
        ...state,
        ...markDirty(state, "questions"),
        activePage: "question",
        activeQuestionIdx: insertAt,
        data: {
          ...state.data,
          questions: reindexed
        }
      };
    });
  },

  deleteQuestion: () => {
    set((state) => {
      if (!state.data || state.activeQuestionIdx === null) return state;

      const sorted = state.data.questions
        .slice()
        .sort((a, b) => a.index - b.index);

      const deleteIdx = state.activeQuestionIdx;
      const filtered = sorted.filter((q) => q.index !== deleteIdx);

      if (filtered.length === 0)
        return {
          ...state,
          ...markDirty(state, "questions"),
          activeQuestionIdx: null,
          data: { ...state.data, questions: [] }
        };

      const reindexed = filtered.map((q, i) => ({ ...q, index: i }));

      const newActiveIdx = deleteIdx > 0 ? deleteIdx - 1 : 0;

      return {
        ...state,
        ...markDirty(state, "questions"),
        activeQuestionIdx: newActiveIdx,
        data: {
          ...state.data,
          questions: reindexed
        }
      };
    });
  },

  activeConditionIdx: null,

  updateActiveConditionIdx: (activeConditionIdx: number | null) => {
    set((state) => ({ ...state, activeConditionIdx }));
  },

  addNewCondition: () => {
    set((state) => {
      if (!state.data) return state;

      const conditions = state.data.conditions ?? [];

      const blank: DraftSurveyTemplateCondition = {
        internal_id: uuidv4(),
        parent_question_internal_id: "",
        child_question_internal_ids: [],
        action: "",
        condition: {}
      };

      const updated = [...conditions, blank];

      return {
        ...state,
        ...markDirty(state, "conditions"),
        activeConditionIdx: updated.length - 1,
        data: { ...state.data, conditions: updated }
      };
    });
  },

  deleteCondition: (idx: number) => {
    set((state) => {
      if (!state.data) return state;

      const conditions = (state.data.conditions ?? []).filter(
        (_, i) => i !== idx
      );

      let newActiveIdx = state.activeConditionIdx;
      if (state.activeConditionIdx === idx) newActiveIdx = null;
      else if (
        state.activeConditionIdx !== null &&
        state.activeConditionIdx > idx
      )
        newActiveIdx = state.activeConditionIdx - 1;

      return {
        ...state,
        ...markDirty(state, "conditions"),
        activeConditionIdx: newActiveIdx,
        data: { ...state.data, conditions }
      };
    });
  },

  updateActiveCondition: (
    condition:
      | DraftSurveyTemplateCondition
      | ((prev: DraftSurveyTemplateCondition) => DraftSurveyTemplateCondition)
  ) => {
    set((state) => {
      if (!state.data || state.activeConditionIdx === null) return state;

      const conditions = state.data.conditions ?? [];
      const prev = conditions[state.activeConditionIdx];

      const updated =
        typeof condition === "function" ? condition(prev) : condition;

      return {
        ...state,
        ...markDirty(state, "conditions"),
        validationErrors: [],
        data: {
          ...state.data,
          conditions: conditions.map((c, i) =>
            i === state.activeConditionIdx ? updated : c
          )
        }
      };
    });
  }
}));
